import { useState, useEffect } from 'react';
import { Tabs, Space } from 'antd';
import type { TabsProps } from 'antd';
import { Button, Form, Spin } from 'antd';
import { AppstoreOutlined, TeamOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import axios from 'axios';
import { Typography } from '@mui/material';
import SweetAlert from 'sweetalert2';
import { useAuthHeader, useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../Models/Utilisateur';
import { Crypter } from '../../../Functions/Security/Crypter';
import { apiEndpoints } from '../../../config';
import Datas from '../../../Data/Data.json';
import Params from '../../../Types/ParamsPicklist';
import Tab1 from './Tab1';
import Tab2 from './Tab2';
import Tab3 from './Tab3';

let searchGroupUtilis: Params = { CodeAgence: '', Table: 'GROUPUSER', Colonne1: 'CODGROUP', Colonne2: 'LIBELLE', Colagence: '101', Choix: 22 };

const ModuleBIManage = () => {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
  const authHeader = useAuthHeader();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [ajoutActve, setAjoutActif] = useState(false);
  const [modifActve, setModifActve] = useState(true);
  const [enregActve, setEnregActve] = useState(true);
  const [supActive, setSupActive] = useState(true);
  const [impActive, setImpActive] = useState(true);
  const [actuActive, setActActive] = useState(true);
  const [annulActive, setAnnulActive] = useState(true);
  const [consulActive, setConsulActive] = useState(false);
  const [sbmit, setSbmit] = useState('');
  const [isConsultMode, setIsConsultMode] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Module list state
  const [modules, setModules] = useState<any[]>([]);
  const [selectedModule, setSelectedModule] = useState<any>(null);

  // Group assignment state
  const [groupesAffectes, setGroupesAffectes] = useState<any[]>([]);
  const [allGroupes, setAllGroupes] = useState<any[]>([]);

  // Modules par groupe & modules autorisés
  const [modulesParGroupe, setModulesParGroupe] = useState<any[]>([]);
  const [modulesAutorises, setModulesAutorises] = useState<any[]>([]);
  const [selectedCodGroup, setSelectedCodGroup] = useState<string>('');

  useEffect(() => {
    loadModules();
    loadAllGroupes();
  }, []);

  function initializeButton() {
    setAjoutActif(false);
    setModifActve(true);
    setEnregActve(true);
    setSupActive(true);
    setImpActive(true);
    setActActive(true);
    setAnnulActive(true);
    setConsulActive(false);
    setIsConsultMode(false);
    setIsAddMode(false);
    setIsEditMode(false);
  }

  const setStart = () => {
    setSbmit('add');
    setAjoutActif(true);
    setEnregActve(false);
    setAnnulActive(false);
    setConsulActive(true);
    setIsAddMode(true);
    setIsConsultMode(false);
    setIsEditMode(false);
    form.resetFields();
    form.setFieldValue('Actif', true);
    form.setFieldValue('Ordre', 0);
    form.setFieldValue('NiveauMin', 0);
    setSelectedModule(null);
    setGroupesAffectes([]);
  };

  function handleAnnule() {
    form.resetFields();
    setSelectedModule(null);
    setGroupesAffectes([]);
    setModulesParGroupe([]);
    setModulesAutorises([]);
    setSelectedCodGroup('');
    initializeButton();
  }

  function consultButton() {
    setAnnulActive(false);
    setModifActve(false);
    setIsConsultMode(true);
    setIsAddMode(false);
    setIsEditMode(false);
  }

  function modifButton() {
    setSbmit('update');
    setModifActve(true);
    setAjoutActif(true);
    setEnregActve(false);
    setConsulActive(true);
    setIsEditMode(true);
    setIsConsultMode(false);
    setIsAddMode(false);
  }

  // ---- API calls ----

  function loadModules(filter?: { FiltreCategorie?: string; FiltreActif?: boolean | null; FiltreLibelle?: string }) {
    setLoading(true);
    const filterData = {
      FiltreCategorie: filter?.FiltreCategorie || '',
      FiltreActif: filter?.FiltreActif ?? null,
      FiltreLibelle: filter?.FiltreLibelle || '',
    };
    const config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: `${apiEndpoints.moduleBI}/ListModules`,
      headers: {
        Accept: 'text/plain',
        Authorization: authHeader(),
        CypherFilter: Crypter.encrypt(JSON.stringify(filterData)),
      },
    };
    axios
      .request(config)
      .then((response: any) => {
        setLoading(false);
        if (response.data) {
          const res = JSON.parse(Crypter.decrypt(response.data)).Value;
          if (res.StatutCode == 200) {
            const rawModules = res.Result.Table || [];
            const mapped = rawModules.map((m: any) => ({
              CodModule: m.CODMODULE,
              CodCategorie: m.CODCATEGORIE,
              SpName: m.SPNAME,
              Libelle: m.LIBELLE,
              Description: m.DESCRIPTION,
              NiveauMin: m.NIVEAU_MIN,
              Actif: m.ACTIF,
              Ordre: m.ORDRE,
            }));
            setModules(mapped);
          }
        }
      })
      .catch(() => {
        setLoading(false);
      });
  }

  function consult() {
    const codModule = form.getFieldValue('CodModule');
    if (!codModule) {
      SweetAlert.fire({ title: 'Attention', text: 'Veuillez saisir un code module', icon: 'warning' });
      return;
    }
    initializeButton();
    setLoading(true);
    const config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: `${apiEndpoints.moduleBI}/GetModule`,
      headers: {
        Accept: 'text/plain',
        Authorization: authHeader(),
        CypherCodModule: Crypter.encrypt(codModule),
      },
    };
    axios
      .request(config)
      .then((response: any) => {
        setLoading(false);
        if (response.data) {
          const res = JSON.parse(Crypter.decrypt(response.data)).Value;
          if (res.StatutCode == 200) {
            const raw = res.Result.Table[0];
            const moduleData = {
              CodModule: raw.CODMODULE,
              CodCategorie: raw.CODCATEGORIE,
              SpName: raw.SPNAME,
              Libelle: raw.LIBELLE,
              Description: raw.DESCRIPTION,
              NiveauMin: raw.NIVEAU_MIN,
              Actif: raw.ACTIF,
              Ordre: raw.ORDRE,
            };
            form.setFieldsValue(moduleData);
            setSelectedModule(moduleData);
            consultButton();

            const groupes = (res.Result.Table1 || []).map((g: any) => ({
              CodGroup: g.CODGROUP,
              Libelle: g.LibelleGroupe,
              Niveau: g.NIVEAU,
            }));
            setGroupesAffectes(groupes);
          } else {
            SweetAlert.fire({ title: 'Attention', text: res.Message, icon: 'warning' });
          }
        }
      })
      .catch((error) => {
        setLoading(false);
        SweetAlert.fire({ title: 'Erreur', text: String(error), icon: 'error' });
      });
  }

  function loadGroupesParModule(codModule: string) {
    const config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: `${apiEndpoints.moduleBI}/ListGroupesParModule`,
      headers: {
        Accept: 'text/plain',
        Authorization: authHeader(),
        CypherCodModule: Crypter.encrypt(codModule),
      },
    };
    axios.request(config).then((response: any) => {
      if (response.data) {
        const res = JSON.parse(Crypter.decrypt(response.data)).Value;
        if (res.StatutCode == 200) {
          const rawGroupes = res.Result.Table || res.Result || [];
          const groupes = (Array.isArray(rawGroupes) ? rawGroupes : []).map((g: any) => ({
            CodGroup: g.CODGROUP || g.CodGroup,
            Libelle: g.LibelleGroupe || g.Libelle || g.LIBELLE,
            Niveau: g.NIVEAU || g.Niveau,
          }));
          setGroupesAffectes(groupes);
        }
      }
    });
  }

  function loadAllGroupes() {
    const config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: Datas.ApiUrl.AnySearch,
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader(),
        CypherSearch: Crypter.encrypt(JSON.stringify(searchGroupUtilis)),
      },
    };
    axios.request(config).then((response: any) => {
      if (response.data) {
        const res = JSON.parse(Crypter.decrypt(response.data)).Value;
        if (res.StatutCode == 200) {
          const tableData = res.Result.Table || [];
          const groupes = tableData.map((item: any, index: number) => ({
            key: index,
            CodGroup: item[Object.keys(item)[1]],
            Libelle: item[Object.keys(item)[0]],
          }));
          setAllGroupes(groupes);
        }
      }
    });
  }

  function loadModulesParGroupe(codGroup: string) {
    setLoading(true);
    setSelectedCodGroup(codGroup);
    const config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: `${apiEndpoints.moduleBI}/ListModulesParGroupe`,
      headers: {
        Accept: 'text/plain',
        Authorization: authHeader(),
        CypherCodGroup: Crypter.encrypt(codGroup),
      },
    };
    axios
      .request(config)
      .then((response: any) => {
        setLoading(false);
        if (response.data) {
          const res = JSON.parse(Crypter.decrypt(response.data)).Value;
          if (res.StatutCode == 200) {
            const rawModules = res.Result.Table || res.Result || [];
            const mapped = (Array.isArray(rawModules) ? rawModules : []).map((m: any) => ({
              CodModule: m.CODMODULE || m.CodModule,
              CodCategorie: m.CODCATEGORIE || m.CodCategorie,
              SpName: m.SPNAME || m.SpName,
              Libelle: m.LIBELLE || m.Libelle,
              Description: m.DESCRIPTION || m.Description,
              NiveauMin: m.NIVEAU_MIN || m.NiveauMin,
              Actif: m.ACTIF ?? m.Actif,
              Ordre: m.ORDRE || m.Ordre,
            }));
            setModulesParGroupe(mapped);
          } else {
            setModulesParGroupe([]);
            SweetAlert.fire({ title: 'Attention', text: res.Message, icon: 'warning' });
          }
        }
      })
      .catch((error) => {
        setLoading(false);
        SweetAlert.fire({ title: 'Erreur', text: String(error), icon: 'error' });
      });
  }

  function loadModulesAutorises() {
    setLoading(true);
    const moduleData = {
      Coduti: user.coduti,
      Codage: user.codage,
    };
    const config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: `${apiEndpoints.moduleBI}/ListModulesAutorises`,
      headers: {
        Accept: 'text/plain',
        Authorization: authHeader(),
        CypherUser: Crypter.encrypt(JSON.stringify(moduleData)),
      },
    };
    axios
      .request(config)
      .then((response: any) => {
        setLoading(false);
        if (response.data) {
          const res = JSON.parse(Crypter.decrypt(response.data)).Value;
          if (res.StatutCode == 200) {
            const rawModules = res.Result.Table || res.Result || [];
            const mapped = (Array.isArray(rawModules) ? rawModules : []).map((m: any) => ({
              CodModule: m.CODMODULE || m.CodModule,
              CodCategorie: m.CODCATEGORIE || m.CodCategorie,
              SpName: m.SPNAME || m.SpName,
              Libelle: m.LIBELLE || m.Libelle,
              Description: m.DESCRIPTION || m.Description,
              NiveauMin: m.NIVEAU_MIN || m.NiveauMin,
              Actif: m.ACTIF ?? m.Actif,
              Ordre: m.ORDRE || m.Ordre,
            }));
            setModulesAutorises(mapped);
          } else {
            setModulesAutorises([]);
          }
        }
      })
      .catch((error) => {
        setLoading(false);
        SweetAlert.fire({ title: 'Erreur', text: String(error), icon: 'error' });
      });
  }

  const onFinish = (values: any) => {
    const moduleData = {
      ...values,
      UtiSaisie: user.coduti,
    };

    if (sbmit === 'add') {
      SweetAlert.fire({
        title: 'Création du module BI',
        text: `Voulez-vous créer le module ${values.CodModule} ?`,
        showCancelButton: true,
        confirmButtonText: 'Oui',
        confirmButtonColor: '#116599',
        cancelButtonColor: 'darkred',
        cancelButtonText: 'Non',
        showLoaderOnConfirm: true,
        icon: 'question',
      }).then((res) => {
        if (res.isConfirmed) {
          setLoading(true);
          const data = Crypter.encrypt(JSON.stringify(moduleData));
          const config = {
            method: 'post',
            maxBodyLength: Infinity,
            url: `${apiEndpoints.moduleBI}/CreateModule`,
            headers: {
              Authorization: authHeader(),
              'Content-Type': 'application/json',
            },
            data: data,
          };
          axios
            .request(config)
            .then((response: any) => {
              setLoading(false);
              if (response.data) {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                if (res.StatutCode == 200 || res.StatutCode == 201) {
                  SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
                  form.resetFields();
                  initializeButton();
                  loadModules();
                  setSbmit('');
                } else {
                  SweetAlert.fire({ title: 'Attention', text: res.Message, icon: 'warning' });
                }
              }
            })
            .catch((error) => {
              setLoading(false);
              SweetAlert.fire({ title: 'Erreur', text: String(error), icon: 'error' });
            });
        }
      });
    }

    if (sbmit === 'update') {
      SweetAlert.fire({
        title: 'Modification du module BI',
        text: `Voulez-vous modifier le module ${values.CodModule} ?`,
        showCancelButton: true,
        confirmButtonText: 'Oui',
        confirmButtonColor: '#116599',
        cancelButtonColor: 'darkred',
        cancelButtonText: 'Non',
        showLoaderOnConfirm: true,
        icon: 'question',
      }).then((res) => {
        if (res.isConfirmed) {
          setLoading(true);
          const data = Crypter.encrypt(JSON.stringify(moduleData));
          const config = {
            method: 'put',
            maxBodyLength: Infinity,
            url: `${apiEndpoints.moduleBI}/UpdateModule`,
            headers: {
              Authorization: authHeader(),
              'Content-Type': 'application/json',
            },
            data: data,
          };
          axios
            .request(config)
            .then((response: any) => {
              setLoading(false);
              if (response.data) {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                if (res.StatutCode == 200) {
                  SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
                  form.resetFields();
                  initializeButton();
                  loadModules();
                  setSbmit('');
                } else {
                  SweetAlert.fire({ title: 'Attention', text: res.Message, icon: 'warning' });
                }
              }
            })
            .catch((error) => {
              setLoading(false);
              SweetAlert.fire({ title: 'Erreur', text: String(error), icon: 'error' });
            });
        }
      });
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    const errors = errorInfo.errorFields.map((error: any) => error.name.join(' - '));
    SweetAlert.fire({
      title: 'Veuillez remplir tous les champs obligatoires',
      text: errors.toString().replaceAll(',', ' - '),
      icon: 'warning',
      confirmButtonText: 'OK',
    });
  };

  function supprimer() {
    const codModule = form.getFieldValue('CodModule');
    if (!codModule) {
      SweetAlert.fire({ title: 'Attention', text: 'Veuillez consulter un module d\'abord', icon: 'warning' });
      return;
    }
    SweetAlert.fire({
      title: 'Suppression du module BI',
      text: `Voulez-vous supprimer le module ${codModule} ?`,
      showCancelButton: true,
      confirmButtonText: 'Oui',
      confirmButtonColor: 'darkred',
      cancelButtonColor: '#6c757d',
      cancelButtonText: 'Non',
      showLoaderOnConfirm: true,
      icon: 'warning',
    }).then((res) => {
      if (res.isConfirmed) {
        setLoading(true);
        const moduleData = {
          CodModule: codModule,
          HardDelete: false,
        };
        const data = Crypter.encrypt(JSON.stringify(moduleData));
        const config = {
          method: 'delete',
          maxBodyLength: Infinity,
          url: `${apiEndpoints.moduleBI}/DeleteModule`,
          headers: {
            Authorization: authHeader(),
            'Content-Type': 'application/json',
          },
          data: data,
        };
        axios
          .request(config)
          .then((response: any) => {
            setLoading(false);
            if (response.data) {
              const res = JSON.parse(Crypter.decrypt(response.data)).Value;
              if (res.StatutCode == 200) {
                SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
                form.resetFields();
                setSelectedModule(null);
                setGroupesAffectes([]);
                initializeButton();
                loadModules();
              } else {
                SweetAlert.fire({ title: 'Attention', text: res.Message, icon: 'warning' });
              }
            }
          })
          .catch((error) => {
            setLoading(false);
            SweetAlert.fire({ title: 'Erreur', text: String(error), icon: 'error' });
          });
      }
    });
  }

  function modif() {
    modifButton();
  }

  function actualiser() {
    loadModules();
    loadAllGroupes();
    SweetAlert.fire({ title: 'Actualisé', text: 'Les données ont été actualisées', icon: 'success', timer: 1500, showConfirmButton: false });
  }

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: (
        <span id='Tab'>
          <AppstoreOutlined />
          Informations du module
        </span>
      ),
      children: (
        <Tab1
          form={form}
          disabled={!ajoutActve}
          isConsultMode={isConsultMode}
          isAddMode={isAddMode}
          isEditMode={isEditMode}
          modules={modules}
          selectedModule={selectedModule}
          onSelectModule={(mod: any) => {
            form.setFieldsValue(mod);
            setSelectedModule(mod);
            consultButton();
            loadGroupesParModule(mod.CodModule);
          }}
          onFilter={(filter) => loadModules(filter)}
          onConsult={consult}
        />
      ),
    },
    {
      key: '2',
      label: (
        <span id='Tab'>
          <TeamOutlined />
          Affectation des groupes
        </span>
      ),
      children: (
        <Tab2
          form={form}
          disabled={!ajoutActve || isAddMode}
          isConsultMode={isConsultMode}
          selectedModule={selectedModule}
          groupesAffectes={groupesAffectes}
          setGroupesAffectes={setGroupesAffectes}
          allGroupes={allGroupes}
          authHeader={authHeader}
          user={user}
        />
      ),
    },
    {
      key: '3',
      label: (
        <span id='Tab'>
          <SafetyCertificateOutlined />
          Modules par groupe & autorisés
        </span>
      ),
      children: (
        <Tab3
          allGroupes={allGroupes}
          modulesParGroupe={modulesParGroupe}
          modulesAutorises={modulesAutorises}
          selectedCodGroup={selectedCodGroup}
          onLoadModulesParGroupe={loadModulesParGroupe}
          onLoadModulesAutorises={loadModulesAutorises}
          authHeader={authHeader}
          user={user}
        />
      ),
    },
  ];

  return (
    <div className='tab'>
      <Typography align='center' className='formTitle'>
        <b>GESTION DES MODULES BI</b>
      </Typography>
      <Spin spinning={loading} size='large' style={{ color: '#116599' }}>
        <Form
          disabled={!ajoutActve}
          autoComplete='off'
          labelAlign='left'
          form={form}
          name='moduleBI_form'
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          style={{ minWidth: 'auto', maxWidth: 'auto' }}
        >
          <div className='bG'>
            <legend>
              <Typography color={'#116599'}>Actions</Typography>
            </legend>
            <Form.Item shouldUpdate className='bouttong'>
              <Space direction='vertical' style={{ width: '100%' }}>
                <Button size='large' className='bt_add' disabled={consulActive} onClick={consult} type='ghost' htmlType='button' block>
                  Consulter
                </Button>
                <Button size='large' className='bt_add' disabled={ajoutActve} onClick={setStart} type='ghost' htmlType='button' block>
                  Ajouter
                </Button>
                <Button size='large' className='bt_add' disabled={modifActve} onClick={modif} type='ghost' htmlType='button' block>
                  Modifier
                </Button>
                <Button size='large' className='bt_add' disabled={enregActve} type='ghost' htmlType='submit' block>
                  Enregistrer
                </Button>
                <Button size='large' className='bt_add' onClick={supprimer} disabled={supActive} type='ghost' htmlType='button' block>
                  Supprimer
                </Button>
                <Button size='large' className='bt_add' disabled={impActive} type='ghost' htmlType='button' block>
                  Imprimer
                </Button>
                <Button size='large' className='bt_add' disabled={actuActive} onClick={actualiser} type='ghost' htmlType='button' block>
                  Actualiser
                </Button>
                <Button size='large' disabled={false} onClick={handleAnnule} className='bt_add' type='ghost' htmlType='reset' block>
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          <div className='clientP'>
            <div>
              <Tabs size='large' type='card' defaultActiveKey='1' items={items} />
            </div>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default ModuleBIManage;
